import React from 'react';
import { MandatoryTopicReminderType } from '../../../../../generated/core-api/graphql-react-query';
import { SessionComplianceChecklistItem } from '../../Contexts/SessionComplianceContextProvider/type';
import useSessionComplianceContext from '../../Contexts/SessionComplianceContextProvider/useSessionComplianceContext';
import useSessionStoreContext from '../../Contexts/SessionStoreContextProvider/useSessionStoreContext';
import useTriggerReminder from './useTriggerReminder';

export default function useComplianceReminder() {
  const { sessionId: [sessionId] } = useSessionStoreContext();
  const triggerReminder = useTriggerReminder();
  const [intervalIds, setIntervalIds] = React.useState<{ intervalId: NodeJS.Timeout, topicTextLower: string }[]>([]);
  const complianceChecklist = useSessionComplianceContext();

  const addInterval = React.useCallback((topic: SessionComplianceChecklistItem) => {
    if (!topic.reminder) {
      throw new Error('trying to add interval on topic that has no reminder setting');
    }
    if (!topic.reminder.type) {
      throw new Error('Reminder type is not defined');
    }
    const existingInterval = intervalIds.find((item) => item.topicTextLower === topic.topic.textLower);
    if (!existingInterval) {
      const intervalMethod = topic.reminder.type === MandatoryTopicReminderType.FixedTime ? setTimeout : setInterval;
      const delayUntilReminder = topic.reminder.time * 60 * 1000;
      const interval = intervalMethod(() => { triggerReminder(topic); }, delayUntilReminder);
      setIntervalIds((prev) => [...prev, { intervalId: interval, topicTextLower: topic.topic.textLower, type: topic.reminder?.type }]);
    }
  }, [intervalIds]);

  const removeInterval = React.useCallback((intervalId: NodeJS.Timeout) => {
    clearInterval(intervalId);
    setIntervalIds((prev) => prev.filter((item) => item.intervalId !== intervalId));
  }, []);

  React.useEffect(() => {
    if (!sessionId) {
      intervalIds.forEach((item) => removeInterval(item.intervalId));
      return;
    }

    complianceChecklist.forEach((topic) => {
      if (topic.reminder && topic.requiredAt) {
        if (topic.result !== null) {
          const intervalId = intervalIds.find((item) => item.topicTextLower === topic.topic.textLower);
          if (intervalId) {
            removeInterval(intervalId.intervalId);
          }
        } else {
          addInterval(topic);
        }
      }
    });
  }, [!!sessionId, complianceChecklist]);
}
